/**
 * 0.37.62 — Route wrapper for the language-prefixed public URL
 * pattern `/:lang/p/:slug`.
 *
 * Admin asked for public URLs to carry a language segment right
 * after the domain: `caf.govtech.am/hy/p/foo`,
 * `caf.govtech.am/en/p/foo`, etc. External links that used to
 * land visitors on whatever language their browser suggested
 * now land them explicitly on the language the SHARER intended.
 *
 * Responsibilities:
 *
 *   1. Read `:lang` from useParams.
 *   2. Validate against the enabled languages loaded by
 *      LanguageContext. Enabled languages arrive asynchronously,
 *      so during initial load `languages` may be [] — while it
 *      is, we render StaticPage optimistically (the context is
 *      still using its localStorage / fallback lang). Once the
 *      list resolves and the URL lang is proven bad, we replace
 *      the URL with the currently-active language.
 *   3. When the URL lang is valid and differs from the context
 *      lang, call `setLanguageLocal(lang)` so the whole app
 *      re-renders in that language. `setLanguageLocal` (not
 *      `setLanguage`) is deliberate: it updates the UI + local
 *      storage without POSTing to the user's server-side
 *      preferred_language. Visiting a shared link in a different
 *      language shouldn't silently rewrite the visitor's saved
 *      preference — only an explicit click in the language
 *      dropdown does that.
 *   4. Render <StaticPage /> — StaticPage reads the slug via
 *      useParams itself, so no props needed.
 */
import { appPath } from '../lib/basePath';
import { useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import StaticPage from '../pages/StaticPage';

export default function LangStaticPageRoute() {
  const { lang, slug } = useParams();
  const { language, setLanguageLocal, languages } = useLanguage();

  // Sync context to URL when URL is valid.
  useEffect(() => {
    if (!lang || !languages || languages.length === 0) return;
    const isEnabled = languages.some(l => l.code === lang);
    if (isEnabled && lang !== language) {
      setLanguageLocal(lang);
    }
  }, [lang, language, languages, setLanguageLocal]);

  // Bad lang code AND we've already loaded the enabled list —
  // rewrite the URL to whatever language the visitor's context
  // ended up at (from localStorage / server preference / default).
  // While `languages` is still loading, we cannot tell if the URL
  // lang is bad — render optimistically instead of flickering to
  // a redirect and back.
  if (Array.isArray(languages) && languages.length > 0) {
    const isEnabled = languages.some(l => l.code === lang);
    if (!isEnabled) {
      // 0.42.72 - Only the LANGUAGE is wrong here: keep the prefix the
      // citizen asked for, or a services page bounces to a dead /p/.
      return <Navigate to={`/${language}/${appPath().split('/')[2] === 'services' ? 'services' : 'p'}/${slug}`} replace />;
    }
  }

  // 0.49.25 - A fresh page for each slug: moving between pages no longer
  // reloads the site, so nothing of the last page's state may carry over.
  return <StaticPage key={slug} />;
}
